import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import Layout from './components/Layout';
import { isAdmin as isAdminRole } from './utils/roles';
import './App.css';

// ── Lazy-load every page so only the current page's JS is loaded ──
// This reduces initial bundle from ~2MB → ~200KB (10x faster first load)
const Dashboard       = lazy(() => import('./pages/Dashboard'));
const Orders          = lazy(() => import('./pages/Orders'));
const Products        = lazy(() => import('./pages/Products'));
const Categories      = lazy(() => import('./pages/Categories'));
const Billing         = lazy(() => import('./pages/Billing'));
const Inventory       = lazy(() => import('./pages/Inventory'));
const Purchases       = lazy(() => import('./pages/Purchases'));
const Reports         = lazy(() => import('./pages/Reports'));
const Coupons         = lazy(() => import('./pages/Coupons'));
const Customers       = lazy(() => import('./pages/Customers'));
const Suppliers       = lazy(() => import('./pages/Suppliers'));
const Expenses        = lazy(() => import('./pages/Expenses'));
const Staff           = lazy(() => import('./pages/Staff'));
const LiveTracking    = lazy(() => import('./pages/LiveTracking'));
const Returns         = lazy(() => import('./pages/Returns'));
const ExpiredStock    = lazy(() => import('./pages/ExpiredStock'));
const SupplierReturns = lazy(() => import('./pages/SupplierReturns'));
const CustomerLoyalty = lazy(() => import('./pages/CustomerLoyalty'));
const Attendance      = lazy(() => import('./pages/Attendance'));
const CashDrawer      = lazy(() => import('./pages/CashDrawer'));
const Payroll         = lazy(() => import('./pages/Payroll'));
const Banners         = lazy(() => import('./pages/Banners'));
const Offers          = lazy(() => import('./pages/Offers'));
const Sessions        = lazy(() => import('./pages/Sessions'));
const Login           = lazy(() => import('./pages/Login'));

// ── Page loader shown while a lazy chunk is downloading ──
const PageLoader = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '60vh', flexDirection: 'column', gap: 12
  }}>
    <div style={{
      width: 36, height: 36,
      border: '3px solid #e2e8f0',
      borderTopColor: '#6366f1',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite'
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,       // cache data for 30s → no re-fetch on tab switch
      refetchOnWindowFocus: false,
    }
  }
});

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  return token ? children : <Navigate to="/login" />;
};

// Owner/admin-only page guard. A cashier who types an admin URL is bounced
// to the billing counter (their home). The backend enforces this too — this
// is the UI half so cashiers never even load the page.
const RequireAdmin = ({ children }) => (
  isAdminRole() ? children : <Navigate to="/billing" replace />
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{
          style: { background: '#1a1a2e', color: '#fff', border: '1px solid #16213e' }
        }} />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
              {/* Cashier home = billing. Dashboard is owner-only, so a cashier
                  hitting "/" is redirected to the counter. */}
              <Route index                    element={<RequireAdmin><Suspense fallback={<PageLoader />}><Dashboard /></Suspense></RequireAdmin>} />
              {/* ── Cashier-accessible pages ── */}
              <Route path="orders"            element={<Suspense fallback={<PageLoader />}><Orders /></Suspense>} />
              <Route path="billing"           element={<Suspense fallback={<PageLoader />}><Billing /></Suspense>} />
              <Route path="returns"           element={<Suspense fallback={<PageLoader />}><Returns /></Suspense>} />
              <Route path="purchases"         element={<Suspense fallback={<PageLoader />}><Purchases /></Suspense>} />
              {/* ── Owner/admin-only pages ── */}
              <Route path="products"          element={<RequireAdmin><Suspense fallback={<PageLoader />}><Products /></Suspense></RequireAdmin>} />
              <Route path="categories"        element={<RequireAdmin><Suspense fallback={<PageLoader />}><Categories /></Suspense></RequireAdmin>} />
              <Route path="inventory"         element={<RequireAdmin><Suspense fallback={<PageLoader />}><Inventory /></Suspense></RequireAdmin>} />
              <Route path="reports"           element={<RequireAdmin><Suspense fallback={<PageLoader />}><Reports /></Suspense></RequireAdmin>} />
              <Route path="coupons"           element={<RequireAdmin><Suspense fallback={<PageLoader />}><Coupons /></Suspense></RequireAdmin>} />
              <Route path="customers"         element={<RequireAdmin><Suspense fallback={<PageLoader />}><Customers /></Suspense></RequireAdmin>} />
              <Route path="suppliers"         element={<RequireAdmin><Suspense fallback={<PageLoader />}><Suppliers /></Suspense></RequireAdmin>} />
              <Route path="expenses"          element={<RequireAdmin><Suspense fallback={<PageLoader />}><Expenses /></Suspense></RequireAdmin>} />
              <Route path="staff"             element={<RequireAdmin><Suspense fallback={<PageLoader />}><Staff /></Suspense></RequireAdmin>} />
              <Route path="live-tracking"     element={<RequireAdmin><Suspense fallback={<PageLoader />}><LiveTracking /></Suspense></RequireAdmin>} />
              <Route path="expired-stock"     element={<RequireAdmin><Suspense fallback={<PageLoader />}><ExpiredStock /></Suspense></RequireAdmin>} />
              <Route path="supplier-returns"  element={<RequireAdmin><Suspense fallback={<PageLoader />}><SupplierReturns /></Suspense></RequireAdmin>} />
              <Route path="loyalty"           element={<RequireAdmin><Suspense fallback={<PageLoader />}><CustomerLoyalty /></Suspense></RequireAdmin>} />
              <Route path="attendance"        element={<RequireAdmin><Suspense fallback={<PageLoader />}><Attendance /></Suspense></RequireAdmin>} />
              <Route path="cash-drawer"       element={<RequireAdmin><Suspense fallback={<PageLoader />}><CashDrawer /></Suspense></RequireAdmin>} />
              <Route path="payroll"           element={<RequireAdmin><Suspense fallback={<PageLoader />}><Payroll /></Suspense></RequireAdmin>} />
              <Route path="banners"           element={<RequireAdmin><Suspense fallback={<PageLoader />}><Banners /></Suspense></RequireAdmin>} />
              <Route path="offers"            element={<RequireAdmin><Suspense fallback={<PageLoader />}><Offers /></Suspense></RequireAdmin>} />
              <Route path="sessions"          element={<RequireAdmin><Suspense fallback={<PageLoader />}><Sessions /></Suspense></RequireAdmin>} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
